import { Signal } from '@angular/core';
import { PartnerTypes } from '@config';
import { IUserService, UserValue } from '@core-features/core';
import { map, Observable } from 'rxjs';
import { UserPermissions } from './user-permissions';

export abstract class UserBaseService implements IUserService {
  abstract user$: Observable<UserValue>;
  abstract $user: Signal<UserValue>;

  /**
   * Uploads the user's signature to the identity service, then refreshes the
   * user info. Abstract so the generated identity client is injected in the
   * concrete service (resolved in the app's configured root injector), not
   * eagerly in this base — which would break the plain-`new` mocks (no
   * injection context).
   */
  abstract uploadSignature(signature: Blob): Observable<void>;

  private _hasAllRolesCalc(user: UserValue, roles: string[]): boolean {
    if (user?.roles && user.roles.length > 0 && roles && roles.length > 0) {
      for (const reqRole of roles) {
        if (user.roles.indexOf(reqRole) === -1) {
          return false;
        }
      }
      return true;
    }
    return false;
  }

  private _hasApplicationCalc(user: UserValue, appId: string): boolean {
    if (user?.roles && appId) {
      return !!user.roles.find((x) =>
        x.toLowerCase().startsWith(appId.toLowerCase()),
      );
    }
    return false;
  }

  private _isGermanyCalc(user: UserValue): boolean {
    return user?.area_sapid_nr === '0000001104';
  }

  hasOneRole(roles: string[] | undefined): boolean {
    return UserPermissions.hasOneRole(this.$user(), roles);
  }

  hasOneRole$(roles: string[] | undefined): Observable<boolean> {
    return this.user$.pipe(
      map((user) => UserPermissions.hasOneRole(user, roles)),
    );
  }

  hasRole(role: string): boolean {
    const roles: string[] = [role];
    return UserPermissions.hasOneRole(this.$user(), roles);
  }

  hasRole$(role: string): Observable<boolean> {
    const roles: string[] = [role];
    return this.user$.pipe(
      map((user) => UserPermissions.hasOneRole(user, roles)),
    );
  }

  hasAllRoles(roles: string[]): boolean {
    return this._hasAllRolesCalc(this.$user(), roles);
  }

  hasAllRoles$(roles: string[]): Observable<boolean> {
    return this.user$.pipe(map((user) => this._hasAllRolesCalc(user, roles)));
  }

  hasApplication(appId: string): boolean {
    return this._hasApplicationCalc(this.$user(), appId);
  }

  hasApplication$(appId: string): Observable<boolean> {
    return this.user$.pipe(
      map((user) => this._hasApplicationCalc(user, appId)),
    );
  }

  isOneOfPartnerTypes(partnerTypes: number[]): boolean {
    return UserPermissions.isOneOfPartnerTypes(this.$user(), partnerTypes);
  }

  isOneOfPartnerTypes$(partnerTypes: number[]): Observable<boolean> {
    return this.user$.pipe(
      map((user) => UserPermissions.isOneOfPartnerTypes(user, partnerTypes)),
    );
  }

  isPartnerType(partnerType: number) {
    const partnerTypes: number[] = [partnerType];
    return UserPermissions.isOneOfPartnerTypes(this.$user(), partnerTypes);
  }

  isPartnerType$(partnerType: number): Observable<boolean> {
    const partnerTypes: number[] = [partnerType];
    return this.user$.pipe(
      map((user) => UserPermissions.isOneOfPartnerTypes(user, partnerTypes)),
    );
  }

  isAG() {
    return this.isPartnerType(PartnerTypes.AG);
  }

  isAG$() {
    return this.isPartnerType$(PartnerTypes.AG);
  }

  isArea() {
    return this.isPartnerType(PartnerTypes.AREA);
  }

  isArea$() {
    return this.isPartnerType$(PartnerTypes.AREA);
  }

  isGeneralAgent() {
    return this.isPartnerType(PartnerTypes.GeneralAgent);
  }

  isGeneralAgent$() {
    return this.isPartnerType$(PartnerTypes.GeneralAgent);
  }

  isServicePartner() {
    return this.isPartnerType(PartnerTypes.ServicePartner);
  }

  isServicePartner$() {
    return this.isPartnerType$(PartnerTypes.ServicePartner);
  }

  isEndCustomer() {
    return this.isPartnerType(PartnerTypes.EndCustomer);
  }

  isEndCustomer$() {
    return this.isPartnerType$(PartnerTypes.EndCustomer);
  }

  isSupplier() {
    return this.isPartnerType(PartnerTypes.Supplier);
  }

  isSupplier$() {
    return this.isPartnerType$(PartnerTypes.Supplier);
  }

  hasNoPartnerType() {
    return this.$user()?.partnertype === undefined;
  }

  isInUserGroup(userGroup: string): boolean {
    const userGroups: string[] = [userGroup];
    return UserPermissions.isInOneOfUserGroups(this.$user(), userGroups);
  }

  isInUserGroup$(userGroup: string): Observable<boolean> {
    const userGroups: string[] = [userGroup];
    return this.user$.pipe(
      map((user) => UserPermissions.isInOneOfUserGroups(user, userGroups)),
    );
  }

  isInOneOfUserGroups(userGroups: string[]): boolean {
    return UserPermissions.isInOneOfUserGroups(this.$user(), userGroups);
  }

  isInOneOfUserGroups$(userGroups: string[]): Observable<boolean> {
    return this.user$.pipe(
      map((user) => UserPermissions.isInOneOfUserGroups(user, userGroups)),
    );
  }

  isGermany(): boolean {
    return this._isGermanyCalc(this.$user());
  }

  isGermany$(): Observable<boolean> {
    return this.user$.pipe(map((user) => this._isGermanyCalc(user)));
  }

  language() {
    return this.$user()?.lang || 'EN';
  }
}
